/** Utilities for grouping tasks into daily sections and calculating durations. */
// @ts-check
import { formatHours } from '@/components/ui/formatHours';
import { toLocalDateStr } from '@/features/today/utils/completionStreakUtils';
import {
  getCapacityDurationMinutes,
  isSchedulableTask,
} from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * @returns {{ startOfToday: number, endOfToday: number }}
 */
export const getDayBoundaries = () => {
  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  ).getTime();
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();
  return { startOfToday, endOfToday };
};

/**
 * @param {Task} task
 * @returns {number}
 */
export const getTaskTime = (task) => {
  const raw = task?.date;
  if (!raw) return NaN;
  if (raw.toDate) return raw.toDate().getTime();
  if (typeof raw === 'string' && ISO_DATE_ONLY.test(raw)) {
    return new Date(`${raw}T00:00:00`).getTime();
  }
  return new Date(raw).getTime();
};

/**
 * @param {Task} task
 * @param {number} start
 * @param {number} end
 * @returns {boolean}
 */
const isTaskTimeToday = (task, start, end) => {
  const taskTime = getTaskTime(task);
  if (Number.isNaN(taskTime)) return false;
  return taskTime >= start && taskTime <= end;
};

/**
 * @param {Task} task
 * @param {number} startOfToday
 * @param {number} endOfToday
 * @returns {boolean}
 */
const isCompletedToday = (task, startOfToday, endOfToday) => {
  const todayStr = toLocalDateStr(new Date(startOfToday));
  if (!task?.completed || !todayStr) return false;

  const completedDay = toLocalDateStr(task.completedAt);
  if (completedDay) return completedDay === todayStr;

  if (!task.date) return false;
  return isTaskTimeToday(task, startOfToday, endOfToday);
};

/**
 * @param {Task[]} filteredTasks
 * @param {number} startOfToday
 * @param {Map<string, Task>} [tasksMap]
 * @returns {Task[]}
 */
export const getOverdueTasks = (
  filteredTasks,
  startOfToday,
  tasksMap = new Map(),
) => {
  if (!filteredTasks) return [];
  return filteredTasks
    .filter((task) => {
      if (!task.date || task.completed || task.status === 'Backlog')
        return false;
      if (!isSchedulableTask(task, tasksMap)) return false;
      return getTaskTime(task) < startOfToday;
    })
    .sort((a, b) => {
      const dateA = getTaskTime(a);
      const dateB = getTaskTime(b);
      if (dateA !== dateB) return dateA - dateB;
      if (a.time && b.time) {
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
      }
      const keyA = a.orderKey || '';
      const keyB = b.orderKey || '';
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
};

/**
 * @param {Task[]} filteredTasks
 * @param {number} startOfToday
 * @param {number} endOfToday
 * @param {Map<string, Task>} [tasksMap]
 * @returns {Task[]}
 */
export const getTodayTasks = (
  filteredTasks,
  startOfToday,
  endOfToday,
  tasksMap = new Map(),
) => {
  if (!filteredTasks) return [];
  return filteredTasks
    .filter((task) => {
      if (!task.date || task.completed || task.status === 'Backlog')
        return false;
      if (!isSchedulableTask(task, tasksMap)) return false;
      return isTaskTimeToday(task, startOfToday, endOfToday);
    })
    .sort((a, b) => {
      if (a.time && b.time) {
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
        return 0;
      }
      if (a.time) return -1;
      if (b.time) return 1;
      const keyA = a.orderKey || '';
      const keyB = b.orderKey || '';
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
};

/**
 * @param {Task[]} filteredTasks
 * @param {number} startOfToday
 * @param {number} endOfToday
 * @returns {Task[]}
 */
export const getCompletedTodayTasks = (
  filteredTasks,
  startOfToday,
  endOfToday,
) => {
  if (!filteredTasks) return [];
  return filteredTasks
    .filter((task) => isCompletedToday(task, startOfToday, endOfToday))
    .sort((a, b) => {
      if (a.time && b.time) {
        if (a.time < b.time) return -1;
        if (a.time > b.time) return 1;
        return 0;
      }
      if (a.time) return -1;
      if (b.time) return 1;
      const keyA = a.orderKey || '';
      const keyB = b.orderKey || '';
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });
};

/**
 * @param {Task[]} filteredTasks
 * @param {number} endOfToday
 * @returns {Task[]}
 */
export const getUpcomingTasks = (filteredTasks, endOfToday) => {
  if (!filteredTasks) return [];
  return filteredTasks
    .filter((task) => {
      if (
        !task.date ||
        task.completed ||
        task.status === 'Backlog' ||
        task.person
      )
        return false;
      return getTaskTime(task) > endOfToday;
    })
    .sort((a, b) => {
      const dateA = getTaskTime(a);
      const dateB = getTaskTime(b);
      if (dateA !== dateB) return dateA - dateB;
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      if (timeA < timeB) return -1;
      if (timeA > timeB) return 1;
      return 0;
    })
    .slice(0, 3);
};

/**
 * @param {Task[]} tasks
 * @param {Map<string, Task>} tasksMap
 * @returns {number}
 */
const calculateSchedulableDuration = (tasks, tasksMap) => {
  const schedulable = tasks.filter((task) => isSchedulableTask(task, tasksMap));
  return schedulable.reduce(
    (sum, task) =>
      sum + getCapacityDurationMinutes(task, schedulable, tasksMap),
    0,
  );
};

/**
 * @param {Task[]} overdueTasks
 * @param {Task[]} todayTasks
 * @param {Task[]} nextActions
 * @returns {string | null}
 */
export const getTopActionableTaskId = (
  overdueTasks,
  todayTasks,
  nextActions,
) => {
  if (overdueTasks.length > 0) return overdueTasks[0].id;
  if (todayTasks.length > 0) return todayTasks[0].id;
  if (nextActions && nextActions.length > 0) return nextActions[0].id;
  return null;
};

/**
 * @param {string | null} activeFocusTaskId
 * @param {Task[]} filteredTasks
 * @returns {Task | null}
 */
export const getHeroTask = (activeFocusTaskId, filteredTasks) => {
  if (!activeFocusTaskId || !filteredTasks) return null;
  return filteredTasks.find((t) => t.id === activeFocusTaskId) || null;
};

/**
 * @param {Task[]} tasks
 * @param {string | null} activeFocusTaskId
 * @returns {Task[]}
 */
export const filterOutActiveFocusTask = (tasks, activeFocusTaskId) => {
  if (!activeFocusTaskId) return tasks || [];
  return (tasks || []).filter((t) => t.id !== activeFocusTaskId);
};

/**
 * @param {Task[]} tasks
 * @param {Map<string, Task>} tasksMap
 * @returns {string}
 */
export const getDailySectionStats = (tasks, tasksMap = new Map()) => {
  const duration = calculateSchedulableDuration(tasks, tasksMap);
  return duration > 0 ? formatHours(duration / 60) : '';
};
